import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";

type SidebarContextValue = {
  /** Whether the sidebar is expanded (236px) vs collapsed (64px icon rail). Only relevant at md. */
  expanded: boolean;
  /** Whether the mobile/tablet drawer overlay is open. */
  mobileOpen: boolean;
  /** Toggle expanded state (used at md breakpoint). */
  toggle: () => void;
  /** Open the mobile drawer overlay. */
  openMobile: () => void;
  /** Close the mobile drawer overlay, returning focus to the trigger. */
  closeMobile: () => void;
  /** Ref to attach to the hamburger button for focus return. */
  triggerRef: React.RefObject<HTMLButtonElement | null>;
};

const SidebarContext = createContext<SidebarContextValue | undefined>(
  undefined,
);

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [expanded, setExpanded] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const toggle = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const openMobile = useCallback(() => {
    setMobileOpen(true);
  }, []);

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
    // Return focus to the hamburger trigger
    requestAnimationFrame(() => {
      triggerRef.current?.focus();
    });
  }, []);

  return (
    <SidebarContext.Provider
      value={{
        expanded,
        mobileOpen,
        toggle,
        openMobile,
        closeMobile,
        triggerRef,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar(): SidebarContextValue {
  const ctx = useContext(SidebarContext);
  if (!ctx) {
    throw new Error("useSidebar must be used within SidebarProvider");
  }
  return ctx;
}
